import { canRetryQuery, Hub, useErrorMapper } from '@bimone/bimtrack-core';
import { batch, useDispatch } from 'react-redux';
import { useSession } from '../modules/session';
import { useBimTrackApi } from '../../common/hooks/useBimTrackApi';
import { useAuthentication } from '../modules/authentication';
import { BannerTypes, useOverlayBanner } from '@bimone/bimtrackreactcomponents';
import { useHubs } from './useHubs';
import { useQuery } from 'react-query';
import { updateHub } from '../redux/reducers/session';
import HubClient from '../clients/hub/HubClient';
import { useTranslation } from 'react-i18next';
import HttpError from 'standard-http-error';
import { useEffect } from 'react';

export interface UseFetchHubsProps {
  isHubSelectionEnabled?: boolean;
  isReady?: boolean;
  onSettled?: (hubs: Hub[]) => void;
}

export interface UseFetchHubsResult {
  isLoading: boolean;
}

export function useFetchHubs({
  isHubSelectionEnabled = true,
  isReady = true,
  onSettled,
}: UseFetchHubsProps): UseFetchHubsResult {
  const dispatch = useDispatch();
  const session = useSession();
  const bimTrackApi = useBimTrackApi();
  const { accessToken } = useAuthentication();
  const { addBannerFromProps } = useOverlayBanner();
  const { getErrorMessage } = useErrorMapper('commonErrors', 'Unknown');
  const {
    states: { hubs: currentHubs },
    actions: { updateHubs },
  } = useHubs();
  const { t } = useTranslation('navBar');

  const updateStore = (receivedHubs: Hub[]): void => {
    batch(() => {
      dispatch(updateHubs(receivedHubs));
      dispatch(updateHub(receivedHubs.find(current => current.hashId === session?.hub?.hashId)));
    });
  };

  const { isLoading, data } = useQuery(
    ['fetchHubs', isHubSelectionEnabled, bimTrackApi, accessToken],
    async (): Promise<Hub[]> => {
      return new HubClient(bimTrackApi, accessToken).fetchHubs();
    },
    {
      enabled: isHubSelectionEnabled && isReady && !!bimTrackApi && !!accessToken,
      retry: canRetryQuery([HttpError.TOO_MANY_REQUESTS]),
      onError: (error: Error) => {
        updateStore([]);
        addBannerFromProps({
          bannerTitle: t('hubsWithErrorDescription', { error: getErrorMessage(error) }),
          bannerType: BannerTypes.Error,
        });
      },
      onSuccess: hubs => {
        updateStore(hubs);
      },
    }
  );

  useEffect(() => {
    if (isHubSelectionEnabled && isReady && currentHubs && currentHubs === data) {
      onSettled?.(currentHubs);
    }
  }, [currentHubs, data, isHubSelectionEnabled, isReady, onSettled]);

  return { isLoading };
}
